import {Component, OnDestroy, OnInit} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {ApiService, TrackingService} from "../../services/service.index";
import {AuthService} from "../../services/service.index";
import {ActivatedRoute, Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from 'ngx-toastr';

// NGRX
import {Store} from '@ngrx/store';
import {AppState} from 'src/app/app.reducer';
import {FineModel} from 'src/app/store/fine.model';
import {SetFineAction} from 'src/app/store/fine.actions';

import {PersistFineService} from 'src/app/services/persist.fine.service';

import {pipe, Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';

/**
 * After the payment fine before showing the data of the fine and the proof of payment, an endpoint is consulted if that information is ready.
 *
 * @author Fran Aragon
 */
@Component({
    selector: 'app-processingpay',
    templateUrl: './processingpay.component.html',
    styles: []
})
export class ProcessingpayComponent implements OnInit, OnDestroy {

    /**
     * Session hash
     */
    private hash: string;

    /**
     * Param hash
     */
    private param_hash: string;

    /**
     * checksesInterval
     */
    private checksesInterval: any;

    /**
     * Waiting for data ready
     */
    public isWaiting: boolean;

    private subscription: Subscription = new Subscription();

    private fineObject: FineModel;

    private countCalls: number = 0;

    /**
     * @ignore
     */
    constructor(private http: HttpClient,
                private router: Router,
                private route: ActivatedRoute,
                private apiService: ApiService,
                private authService: AuthService,
                private translate: TranslateService,
                private trackingService: TrackingService,
                private toastr: ToastrService,
                private store: Store<AppState>,
                private persistFineService: PersistFineService) {

        this.persistFineService.loadData();

        this.hash = this.authService.getHash();

        this.route.params.subscribe(params => {
            this.param_hash = params['hash'];
        });

        this.isWaiting = true;
    }

    /**
     * Call to endpoint every time interval
     */
    ngOnInit() {

        // if session hash is different from hash of route eject from session to login page.
        if (this.hash != this.param_hash) {
            this.authService.logout();
            this.router.navigate([`/app/login/pyf/${this.hash}`]);
            const errMsg: any = this.translate.instant('LoginPage.ExpiredSession');
            this.toastr.error(errMsg);
            return false;
        }

        this.checksesInterval = setInterval(() => {
            this.hash = this.authService.getHash();
            if (this.hash != this.param_hash) {
                this.authService.logout();
                this.router.navigate([`/app/login/pyf/${this.param_hash}`]);
                const errMsg: any = this.translate.instant('LoginPage.ExpiredSession');
                this.toastr.error(errMsg);
                clearInterval(this.checksesInterval);
                return false;
            }

        }, 1000 * 60); // 60s

        this.subscription = this.store.select('fine_state')
            .pipe(filter(fine_state => fine_state.fine != null))
            .subscribe(fine_state => {
                this.fineObject =  new FineModel({
                    rolAcceso: fine_state.fine.rolAcceso,
                    rolPermiso: fine_state.fine.rolPermiso,
                    acceso: fine_state.fine.acceso,
                    cliente: fine_state.fine.cliente,
                    contrato: fine_state.fine.contrato,
                    fechaInicioContrato: fine_state.fine.fechaInicioContrato,
                    fechaFinContrato: fine_state.fine.fechaFinContrato,
                    expedienteGesthispania: fine_state.fine.expedienteGesthispania,
                    nombreOrganismo: fine_state.fine.nombreOrganismo,
                    codigoSancion: fine_state.fine.codigoSancion,
                    lugar: fine_state.fine.lugar,
                    matricula: fine_state.fine.matricula,
                    tipoInfraccion: fine_state.fine.tipoInfraccion,
                    estado: fine_state.fine.estado,
                    fechaFinEnlace: fine_state.fine.fechaFinEnlace,
                    importeAPagar: fine_state.fine.importeAPagar,
                    importePagado: fine_state.fine.importePagado,
                    isProcessingPay: fine_state.fine.isProcessingPay,
                    idEnlace: fine_state.fine.idEnlace,
                    pagoActivo: fine_state.fine.pagoActivo,
                });

            });


        this.myInitEvent();

        this.countCalls = 0;

        let idinterval = setInterval(() => {

            this.apiService.processingPay(`paying/${ this.hash }`).subscribe(
                (response: any) => {

                    if (response == "true") {
                        this.isWaiting = false;
                        this.countCalls = 0;
                        this.router.navigate([`app/viewfine/${this.hash}`]);
                        clearInterval(idinterval);
                    }

                    this.countCalls++;

                    if(this.countCalls > 50){

                        this.fineObject.isProcessingPay = true;
                        this.store.dispatch(new SetFineAction(this.fineObject));
                        this.persistFineService.persistData(this.fineObject);

                        const errMsg: any = this.translate.instant('ErrorsMessages.ProcessingPay');
                        this.toastr.warning(errMsg);
                        this.router.navigate([`app/viewfine/${this.hash}`]);
                        this.errorEvent("PPCALLS-EXCEEDED", "Number of calls of processing pay exceeded");
                        clearInterval(idinterval);
                    }

                },
                error => {

                    this.errorEvent(error.status, error.message);

                    let errMsg;
                    if (error.status == 401) {
                        errMsg = this.translate.instant('ErrorsMessages.Err401');
                    }

                    if (error.status == 500) {
                        errMsg = this.translate.instant('ErrorsMessages.Err500');
                    }

                    this.toastr.error(errMsg || 'Processing pay error');
                    this.countCalls = 0;
                    clearInterval(idinterval);
                    return false;
                }
            );

        }, 1000 * 3); // 3s

    }

    /**
     * @ignore
     */
    ngOnDestroy() {
        clearInterval(this.checksesInterval);
        this.subscription.unsubscribe();
    }

    /**
     * Captures component error event and sends error event data to analytics.
     */
    errorEvent(status, message) {
        if (this.trackingService.getPermissionTracking() == 'all' || this.trackingService.getPermissionTracking() == 'statistics') {

            this.trackingService.setHash(this.hash);

            this.trackingService.errorEvent(status, message);

            // Google Analytics
            let eventAction: string = "error message: " + message + " - error code status: " + status;
            // (<any>window).ga('send', 'event', {
            //     eventCategory: 'Procesing Pay Error Events',
            //     eventLabel: 'ProcesingPayErrorEvents',
            //     eventAction: eventAction,
            //     eventValue: 10
            // });

        }
    }

    /**
     * Captures component event and sends event data to analytics.
     */
    myInitEvent() {
        if(this.trackingService.getPermissionTracking() == 'all' || this.trackingService.getPermissionTracking() == 'statistics'){

            this.trackingService.setHash(this.hash);

            this.trackingService.returnTPVEvent("processing-pay", 'RETURN_TPV');

            // Google Analytics
            // (<any>window).ga('send', 'event', {
            //     eventCategory: 'Procesing Pay Tpv',
            //     eventLabel: 'ProcesingPayTpv',
            //     eventAction: 'Procesing Pay Tpv',
            //     eventValue: 10
            // });
        }

    }


}
